<template>
  <div class="user-promotions-list">
    <!-- ========================================== -->
    <!-- فیلترها -->
    <!-- ========================================== -->
    <div class="filter-section">
      <div class="filter-row">
        <div class="filter-group">
          <label>کاربر</label>
          <input
              v-model="filters.user_id"
              type="text"
              placeholder="شناسه یا شماره"
              class="filter-input"
              @keyup.enter="loadUserPromotions"
          />
        </div>

        <div class="filter-group">
          <label>وضعیت</label>
          <select v-model="filters.is_used" @change="loadUserPromotions">
            <option value="">همه</option>
            <option value="1">استفاده شده</option>
            <option value="0">استفاده نشده</option>
          </select>
        </div>

        <div class="filter-group">
          <label>لغو</label>
          <select v-model="filters.is_revoked" @change="loadUserPromotions">
            <option value="">همه</option>
            <option value="0">فعال</option>
            <option value="1">لغو شده</option>
          </select>
        </div>

        <button class="btn-search" @click="loadUserPromotions">🔍 جستجو</button>
        <button class="btn-reset" @click="resetFilters">⟳ پاک کردن</button>
      </div>
    </div>

    <!-- ========================================== -->
    <!-- لیست کوپن‌ها (کارت‌محور) -->
    <!-- ========================================== -->
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <span>در حال بارگذاری...</span>
    </div>

    <div v-else-if="userPromotions.length === 0" class="empty-state">
      <div class="empty-icon">🎫</div>
      <h3>هیچ کوپنی یافت نشد</h3>
      <p>با توجه به فیلترهای انتخاب شده، کوپنی یافت نشد.</p>
    </div>

    <div v-else class="cards-grid">
      <div
          v-for="row in userPromotions"
          :key="row.id"
          class="card-item"
          :class="{
          'is-used': row.is_used,
          'is-revoked': row.is_revoked,
          'is-expired': isExpired(row)
        }"
      >
        <!-- ===== هدر کارت ===== -->
        <div class="card-header">
          <div class="card-user">
            <span class="user-name">{{ row.user ? row.user.name : 'کاربر ناشناس' }}</span>
            <span class="user-phone" v-if="row.user && row.user.phone">
              📱 {{ row.user.phone }}
            </span>
          </div>
          <div class="card-status">
            <span v-if="row.is_used" class="badge badge-success">✓ استفاده شده</span>
            <span v-else-if="isExpired(row)" class="badge badge-danger">✕ منقضی</span>
            <span v-else-if="row.is_revoked" class="badge badge-danger">✕ لغو شده</span>
            <span v-else class="badge badge-active">● فعال</span>
          </div>
        </div>

        <!-- ===== بدنه کارت ===== -->
        <div class="card-body">
          <div class="card-row">
            <span class="card-label">جشنواره:</span>
            <span class="card-value">{{ row.promotion ? row.promotion.title : '-' }}</span>
          </div>

          <div class="card-row code-row">
            <span class="card-label">کد کوپن:</span>
            <span class="code-value">{{ row.code }}</span>
            <button class="btn-copy" @click="copyCode(row.code)" title="کپی کد">📋</button>
          </div>

          <div class="card-row">
            <span class="card-label">تخفیف:</span>
            <span class="card-value discount-text">{{ row.discount_text ? row.discount_text : getDiscountText(row) }}</span>
          </div>

          <div class="card-row">
            <span class="card-label">تاریخ انقضا:</span>
            <span class="card-value" :class="{ 'expired-date': isExpired(row) }">
              {{ formatDate(row.expires_at) }}
              <span v-if="!isExpired(row) && !row.is_used && !row.is_revoked" class="days-left">
                ({{ daysLeft(row.expires_at) }} روز مانده)
              </span>
            </span>
          </div>
        </div>

        <!-- ===== فوتر کارت (عملیات) ===== -->
        <div class="card-footer">
          <template v-if="!row.is_used && !row.is_revoked && !isExpired(row)">
            <button class="btn-action btn-use" @click="openUseDialog(row)">
              ✅ استفاده
            </button>
            <button class="btn-action btn-revoke" @click="revokePromotion(row)">
              ❌ لغو
            </button>
          </template>
          <template v-else>
            <span class="no-action">—</span>
          </template>
        </div>
      </div>
    </div>

    <!-- ========================================== -->
    <!-- دیالوگ استفاده از کوپن -->
    <!-- ========================================== -->
    <div v-if="showUseDialog" class="modal-overlay" @click.self="closeUseDialog">
      <div class="modal-content">
        <div class="modal-header">
          <h3>✅ استفاده از کوپن</h3>
          <button class="btn-close" @click="closeUseDialog">✕</button>
        </div>
        <div class="modal-body">
          <div class="modal-info">
            <p><strong>کوپن:</strong> {{ selectedPromotion.code }}</p>
            <p><strong>کاربر:</strong> {{ selectedPromotion.user.name }}</p>
            <p><strong>تخفیف:</strong> {{ selectedPromotion.discount_text ? selectedPromotion.discount_text : getDiscountText(selectedPromotion) }}</p>
          </div>

        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="closeUseDialog">انصراف</button>
          <button class="btn-primary" @click="confirmUsePromotion" :disabled="useLoading">
            {{ useLoading ? '⏳ در حال ثبت...' : 'تأیید استفاده' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import promotionApi from '@/api/promotion';

export default {
  name: 'UserPromotionsList',

  data() {
    return {
      loading: false,
      userPromotions: [],
      filters: {
        user_id: '',
        is_used: null,
        is_revoked: null
      },
      showUseDialog: false,
      useLoading: false,
      selectedPromotion: null,
      useOrderId: null
    };
  },

  mounted() {
    this.loadUserPromotions();
  },

  methods: {
    async loadUserPromotions() {
      this.loading = true;
      try {
        const params = {
          user_id: this.filters.user_id || undefined,
          is_used: this.filters.is_used !== '' ? this.filters.is_used : undefined,
          is_revoked: this.filters.is_revoked !== '' ? this.filters.is_revoked : undefined
        };

        const { data } = await promotionApi.getUserPromotions(params);
        this.userPromotions = data.data || [];
      } catch (error) {
        this.$message?.error('خطا در بارگذاری اطلاعات') || alert('خطا در بارگذاری اطلاعات');
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    isExpired(row) {
      if (!row || !row.expires_at) return false;
      return new Date(row.expires_at) < new Date() && !row.is_used && !row.is_revoked;
    },

    daysLeft(expiresAt) {
      const diff = new Date(expiresAt) - new Date();
      const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
      return days > 0 ? days : 0;
    },

    formatDate(date) {
      if (!date) return '-';
      const d = new Date(date);
      return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    },

    getDiscountText(row) {
      const promotion = row.promotion;
      if (!promotion) return '-';

      switch (promotion.reward_type) {
        case 'percentage':
          let text = `${promotion.reward_value}% تخفیف`;
          if (promotion.max_discount_amount) {
            text += ` (تا سقف ${this.formatPrice(promotion.max_discount_amount)})`;
          }
          return text;
        case 'fixed_amount':
          return `${this.formatPrice(promotion.reward_value)} تخفیف`;
        case 'free_service':
          return '🎁 یک خدمت رایگان';
        default:
          return '-';
      }
    },

    formatPrice(value) {
      if (!value) return '۰';
      return new Intl.NumberFormat('fa-IR').format(value) + ' تومان';
    },

    async copyCode(code) {
      try {
        await navigator.clipboard.writeText(code);
        this.$message?.success('کد کوپن کپی شد!') || alert('کد کوپن کپی شد!');
      } catch {
        const input = document.createElement('input');
        input.value = code;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
        this.$message?.success('کد کوپن کپی شد!') || alert('کد کوپن کپی شد!');
      }
    },

    async revokePromotion(row) {
      if (!confirm(`آیا از لغو کوپن "${row.code}" اطمینان دارید؟`)) return;

      try {
        await promotionApi.revokeUserPromotion(row.id);
        this.$message?.success('کوپن با موفقیت لغو شد') || alert('کوپن با موفقیت لغو شد');
        this.loadUserPromotions();
      } catch (error) {
        this.$message?.error(error.response?.data?.message || 'خطا در لغو کوپن') || alert(error.response?.data?.message || 'خطا در لغو کوپن');
      }
    },

    openUseDialog(row) {
      this.selectedPromotion = row;
      this.useOrderId = null;
      this.showUseDialog = true;
    },

    closeUseDialog() {
      this.showUseDialog = false;
      this.selectedPromotion = null;
      this.useOrderId = null;
    },

    async confirmUsePromotion() {


      this.useLoading = true;
      try {
        await promotionApi.useUserPromotion(this.selectedPromotion.id, {
          reservation_id: this.useOrderId
        });

        this.$message?.success('کوپن با موفقیت استفاده شد') || alert('کوپن با موفقیت استفاده شد');
        this.closeUseDialog();
        this.loadUserPromotions();
      } catch (error) {
        this.$message?.error(error.response?.data?.message || 'خطا در استفاده از کوپن') || alert(error.response?.data?.message || 'خطا در استفاده از کوپن');
      } finally {
        this.useLoading = false;
      }
    },

    resetFilters() {
      this.filters = { user_id: '', is_used: null, is_revoked: null };
      this.loadUserPromotions();
    }
  }
};
</script>

<style scoped>
/* ========================================== */
/* استایل‌های اصلی */
/* ========================================== */
.user-promotions-list {
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  direction: rtl;
}

/* ========================================== */
/* فیلترها */
/* ========================================== */
.filter-section {
  background: #f8f9fa;
  padding: 16px 20px;
  border-radius: 10px;
  margin-bottom: 20px;
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 6px;
}

.filter-group label {
  font-size: 13px;
  color: #606266;
  font-weight: 500;
}

.filter-group select,
.filter-input {
  padding: 6px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  font-size: 13px;
  background: white;
  min-width: 120px;
}

.filter-input {
  min-width: 150px;
}

.filter-input:focus {
  outline: none;
  border-color: #409eff;
  box-shadow: 0 0 0 3px rgba(64, 158, 255, 0.1);
}

.btn-search {
  padding: 6px 20px;
  background: #409eff;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  transition: background 0.2s;
}

.btn-search:hover {
  background: #66b1ff;
}

.btn-reset {
  padding: 6px 16px;
  background: #f5f7fa;
  color: #606266;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  transition: background 0.2s;
}

.btn-reset:hover {
  background: #e4e7ed;
}

/* ========================================== */
/* حالت بارگذاری و خالی */
/* ========================================== */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 20px;
  color: #909399;
  gap: 12px;
}

.spinner {
  width: 36px;
  height: 36px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #409eff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #909399;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 12px;
}

.empty-state h3 {
  color: #2c3e50;
  margin-bottom: 4px;
}

/* ========================================== */
/* گرید کارت‌ها */
/* ========================================== */
.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 16px;
}

/* ========================================== */
/* کارت کوپن */
/* ========================================== */
.card-item {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  transition: all 0.3s;
  border-right: 4px solid #409eff;
}

.card-item:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.10);
}

.card-item.is-used {
  border-right-color: #67c23a;
  opacity: 0.75;
}

.card-item.is-revoked {
  border-right-color: #909399;
  opacity: 0.65;
}

.card-item.is-expired {
  border-right-color: #f56c6c;
  opacity: 0.6;
}

/* ===== هدر کارت ===== */
.card-header {
  padding: 14px 18px 10px 18px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 8px;
}

.card-user {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.user-name {
  font-weight: 600;
  color: #2c3e50;
  font-size: 15px;
}

.user-phone {
  font-size: 12px;
  color: #909399;
}

.card-status {
  display: flex;
  align-items: center;
}

/* ===== بج‌ها ===== */
.badge {
  display: inline-block;
  padding: 2px 12px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
}

.badge-success {
  background: #f0f9eb;
  color: #67c23a;
}

.badge-danger {
  background: #fef0f0;
  color: #f56c6c;
}

.badge-active {
  background: #ecf5ff;
  color: #409eff;
}

/* ===== بدنه کارت ===== */
.card-body {
  padding: 12px 18px;
}

.card-row {
  display: flex;
  align-items: baseline;
  gap: 6px;
  padding: 3px 0;
  font-size: 13px;
  color: #303133;
  flex-wrap: wrap;
}

.card-label {
  font-weight: 500;
  color: #909399;
  min-width: 70px;
}

.card-value {
  color: #303133;
}

.discount-text {
  font-weight: 500;
  color: #409eff;
}

.code-row {
  background: #f5f7fa;
  padding: 6px 10px;
  border-radius: 6px;
  margin: 4px 0;
}

.code-value {
  font-family: 'Courier New', monospace;
  font-weight: bold;
  font-size: 14px;
  color: #2c3e50;
  letter-spacing: 1px;
}

.btn-copy {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  padding: 0 4px;
  transition: transform 0.2s;
}

.btn-copy:hover {
  transform: scale(1.2);
}

.expired-date {
  color: #f56c6c;
}

.days-left {
  color: #67c23a;
  font-size: 12px;
}

/* ===== فوتر کارت (عملیات) ===== */
.card-footer {
  padding: 10px 18px 14px 18px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.btn-action {
  padding: 6px 16px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s;
  flex: 1;
  min-width: 60px;
  text-align: center;
}

.btn-use {
  background: #f0f9eb;
  color: #67c23a;
}
.btn-use:hover {
  background: #67c23a;
  color: white;
}

.btn-revoke {
  background: #fef0f0;
  color: #f56c6c;
}
.btn-revoke:hover {
  background: #f56c6c;
  color: white;
}

.no-action {
  color: #c0c4cc;
  font-size: 13px;
  width: 100%;
  text-align: center;
}

/* ========================================== */
/* مودال استفاده از کوپن */
/* ========================================== */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: fadeIn 0.3s;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.modal-content {
  background: white;
  border-radius: 12px;
  max-width: 450px;
  width: 92%;
  max-height: 90vh;
  overflow-y: auto;
  animation: slideUp 0.3s;
}

@keyframes slideUp {
  from { transform: translateY(30px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
}

.modal-header h3 {
  margin: 0;
  color: #2c3e50;
}

.btn-close {
  background: none;
  border: none;
  font-size: 22px;
  cursor: pointer;
  color: #909399;
  transition: color 0.2s;
}

.btn-close:hover {
  color: #2c3e50;
}

.modal-body {
  padding: 20px;
}

.modal-info {
  background: #f5f7fa;
  padding: 14px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
}

.modal-info p {
  margin: 4px 0;
  color: #606266;
  font-size: 14px;
}

.modal-info strong {
  color: #2c3e50;
}

.form-group {
  margin-top: 12px;
}

.form-group label {
  display: block;
  font-weight: 500;
  color: #2c3e50;
  margin-bottom: 6px;
  font-size: 14px;
}

.form-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
  transition: border-color 0.2s;
}

.form-input:focus {
  outline: none;
  border-color: #409eff;
  box-shadow: 0 0 0 3px rgba(64, 158, 255, 0.1);
}

.modal-footer {
  padding: 16px 20px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

.btn-primary {
  padding: 8px 24px;
  background: #409eff;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.2s;
}

.btn-primary:hover:not(:disabled) {
  background: #66b1ff;
}

.btn-primary:disabled {
  background: #a0cfff;
  cursor: not-allowed;
}

.btn-secondary {
  padding: 8px 24px;
  background: #909399;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.2s;
}

.btn-secondary:hover {
  background: #a6a9ad;
}

/* ========================================== */
/* ریسپانسیو */
/* ========================================== */
@media (max-width: 768px) {
  .filter-row {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-group {
    flex-wrap: wrap;
  }

  .filter-group select,
  .filter-input {
    flex: 1;
    min-width: 80px;
  }

  .btn-search,
  .btn-reset {
    width: 100%;
    justify-content: center;
  }

  .cards-grid {
    grid-template-columns: 1fr;
  }

  .card-header {
    flex-direction: column;
    align-items: stretch;
  }

  .card-footer {
    flex-direction: column;
  }

  .btn-action {
    flex: none;
    width: 100%;
  }

  .modal-content {
    width: 95%;
    margin: 10px;
  }
}

@media (max-width: 480px) {
  .card-body {
    padding: 10px 14px;
  }

  .card-row {
    font-size: 12px;
  }

  .code-value {
    font-size: 13px;
  }

  .modal-footer {
    flex-direction: column;
  }

  .modal-footer .btn-primary,
  .modal-footer .btn-secondary {
    width: 100%;
    text-align: center;
  }
}
</style>